<template>
  <div ref="rootEl" class="dtr">
    <div class="dtr-header">
      <div class="dtr-header-left">
        <icon-base class="dtr-icon" :width="18" :height="18" :width-view-box="24" :height-view-box="24" fill="none"><icon-clock-outline /></icon-base>
        <span class="dtr-summary-label">{{ summaryLabel }}</span>
      </div>
      <span class="dtr-summary-sub">{{ summarySub }}</span>
    </div>

    <div class="dtr-track">
      <div class="dtr-track-fill" :style="trackFillStyle"></div>
    </div>
    <div class="dtr-track-labels">
      <span>{{ maxDays }} days ago</span>
      <span>now</span>
    </div>

    <div class="dtr-body">
      <div class="dtr-presets">
        <div class="dtr-col-label">Quick select</div>
        <button
          v-for="(p, i) in presets"
          :key="p.label"
          type="button"
          class="dtr-preset-btn"
          :class="{ 'is-active': i === activeIndex }"
          @click="selectPreset(i)"
        >{{ p.label }}</button>
      </div>

      <div class="dtr-custom">
        <div class="dtr-col-label">Custom range</div>

        <button type="button" class="dtr-trigger" @click.stop="togglePopover">
          <icon-base class="dtr-icon" :width="18" :height="18" :width-view-box="24" :height-view-box="24" fill="none"><icon-calendar-outline /></icon-base>
          <span class="dtr-trigger-label">{{ triggerLabel }}</span>
          <icon-base class="dtr-icon dtr-icon-right" :class="{ 'is-flipped': popoverOpen }" :width="14" :height="14" :width-view-box="24" :height-view-box="24" fill="none"><icon-chevron-down-small /></icon-base>
        </button>

        <div v-if="popoverOpen" class="dtr-popover" @click.stop>
          <div class="dtr-cal-nav">
            <button type="button" class="dtr-nav-btn" aria-label="Previous month" @click="navMonth(-1)">
              <icon-base :width="16" :height="16" :width-view-box="24" :height-view-box="24" fill="none"><icon-chevron-left-small /></icon-base>
            </button>
            <span class="dtr-month-label">{{ monthLabel }}</span>
            <button type="button" class="dtr-nav-btn" aria-label="Next month" @click="navMonth(1)">
              <icon-base :width="16" :height="16" :width-view-box="24" :height-view-box="24" fill="none"><icon-chevron-right-small /></icon-base>
            </button>
          </div>

          <div class="dtr-cal-hint">{{ selectingEnd ? 'Pick the end date' : 'Pick the start date' }}</div>

          <div class="dtr-cal-dow">
            <span v-for="(d, i) in dowLabels" :key="i">{{ d }}</span>
          </div>

          <div class="dtr-cal-grid">
            <button
              v-for="cell in calendarCells"
              :key="cell.key"
              type="button"
              class="dtr-day-btn"
              :class="dayClass(cell)"
              :disabled="cell.disabled"
              @click="pickDay(cell)"
              @mouseover="hoverDay(cell)"
            >{{ cell.day }}</button>
          </div>

          <div class="dtr-time-row">
            <div class="dtr-time-group">
              <div class="dtr-time-label">From</div>
              <div class="dtr-stepper">
                <div class="dtr-stepper-unit">
                  <button type="button" class="dtr-step-btn" aria-label="Increase from hour" @click="stepTime('start','h',1)"><icon-base :width="13" :height="13" :width-view-box="24" :height-view-box="24" fill="none"><icon-chevron-up-small /></icon-base></button>
                  <div class="dtr-step-val">{{ pad(draftStart.getHours()) }}</div>
                  <button type="button" class="dtr-step-btn" aria-label="Decrease from hour" @click="stepTime('start','h',-1)"><icon-base :width="13" :height="13" :width-view-box="24" :height-view-box="24" fill="none"><icon-chevron-down-small /></icon-base></button>
                </div>
                <span class="dtr-colon">:</span>
                <div class="dtr-stepper-unit">
                  <button type="button" class="dtr-step-btn" aria-label="Increase from minute" @click="stepTime('start','m',1)"><icon-base :width="13" :height="13" :width-view-box="24" :height-view-box="24" fill="none"><icon-chevron-up-small /></icon-base></button>
                  <div class="dtr-step-val">{{ pad(draftStart.getMinutes()) }}</div>
                  <button type="button" class="dtr-step-btn" aria-label="Decrease from minute" @click="stepTime('start','m',-1)"><icon-base :width="13" :height="13" :width-view-box="24" :height-view-box="24" fill="none"><icon-chevron-down-small /></icon-base></button>
                </div>
              </div>
            </div>
            <div class="dtr-time-group">
              <div class="dtr-time-label">To</div>
              <div class="dtr-stepper">
                <div class="dtr-stepper-unit">
                  <button type="button" class="dtr-step-btn" aria-label="Increase to hour" @click="stepTime('end','h',1)"><icon-base :width="13" :height="13" :width-view-box="24" :height-view-box="24" fill="none"><icon-chevron-up-small /></icon-base></button>
                  <div class="dtr-step-val">{{ pad(draftEnd.getHours()) }}</div>
                  <button type="button" class="dtr-step-btn" aria-label="Decrease to hour" @click="stepTime('end','h',-1)"><icon-base :width="13" :height="13" :width-view-box="24" :height-view-box="24" fill="none"><icon-chevron-down-small /></icon-base></button>
                </div>
                <span class="dtr-colon">:</span>
                <div class="dtr-stepper-unit">
                  <button type="button" class="dtr-step-btn" aria-label="Increase to minute" @click="stepTime('end','m',1)"><icon-base :width="13" :height="13" :width-view-box="24" :height-view-box="24" fill="none"><icon-chevron-up-small /></icon-base></button>
                  <div class="dtr-step-val">{{ pad(draftEnd.getMinutes()) }}</div>
                  <button type="button" class="dtr-step-btn" aria-label="Decrease to minute" @click="stepTime('end','m',-1)"><icon-base :width="13" :height="13" :width-view-box="24" :height-view-box="24" fill="none"><icon-chevron-down-small /></icon-base></button>
                </div>
              </div>
            </div>
          </div>

          <button type="button" class="dtr-apply-btn" @click="applyRange">Apply range</button>
        </div>

        <div class="dtr-note">Range limited to the last {{ maxDays }} days (up to {{ formatDT(now) }})</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue'
import IconBase from '../icon/IconBase.vue'
import IconClockOutline from '../icon/IconClockOutline.vue'
import IconCalendarOutline from '../icon/IconCalendarOutline.vue'
import IconChevronDownSmall from '../icon/IconChevronDownSmall.vue'
import IconChevronUpSmall from '../icon/IconChevronUpSmall.vue'
import IconChevronLeftSmall from '../icon/IconChevronLeftSmall.vue'
import IconChevronRightSmall from '../icon/IconChevronRightSmall.vue'
import { instantToWallClock, wallClockToInstant } from '../Analytics/analyticsTimezone'

const props = defineProps({
  maxDays: { type: Number, default: 30 },
  modelValue: { type: Object, default: null }, // { from: Date, to: Date }
  // Optional quick-select overrides: [{ label, mins }] for rolling windows or
  // [{ label, key }] for calendar presets the parent resolves itself (e.g.
  // "Today"). Omit to keep the default rolling list.
  presets: { type: Array, default: null },
  // Preset to highlight initially (matches `key` or `label`). Defaults to the
  // 4th default preset for backwards compatibility, or -1 (custom) when the
  // provided list has no match.
  activePresetKey: { type: String, default: null },
  // IANA zone the calendar and time steppers are read and written in. The
  // component keeps "wall clock" Dates internally (local getters return the
  // zone's fields) and converts to real instants on emit. Null = browser zone.
  timeZone: { type: String, default: null }
})
const emit = defineEmits(['update:modelValue', 'apply'])

const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const dowLabels = ['S','M','T','W','T','F','S']

const tz = props.timeZone || null
const toWall = (d) => instantToWallClock(d, tz)
const fromWall = (d) => wallClockToInstant(d, tz)

const now = toWall(new Date())
const floorDate = new Date(now.getTime() - props.maxDays * 24 * 60 * 60 * 1000)
const minDay = new Date(now); minDay.setDate(minDay.getDate() - props.maxDays); minDay.setHours(0, 0, 0, 0)
const maxDay = new Date(now); maxDay.setHours(0, 0, 0, 0)
const maxMs = props.maxDays * 24 * 60 * 60 * 1000

const defaultPresets = computed(() => [
  { label: 'Last 15 minutes', mins: 15 },
  { label: 'Last hour', mins: 60 },
  { label: 'Last 3 hours', mins: 180 },
  { label: 'Last 24 hours', mins: 1440 },
  { label: 'Last 3 days', mins: 4320 },
  { label: 'Last 7 days', mins: 10080 },
  { label: `Last ${props.maxDays} days`, mins: props.maxDays * 1440 }
])
const presets = computed(() => (Array.isArray(props.presets) && props.presets.length ? props.presets : defaultPresets.value))

function initialActiveIndex() {
  if (props.activePresetKey) {
    const idx = presets.value.findIndex((p) => p.key === props.activePresetKey || p.label === props.activePresetKey)
    return idx
  }
  return props.presets ? -1 : 3
}

const activeIndex = ref(initialActiveIndex())
const initialFrom = props.modelValue?.from ? toWall(props.modelValue.from) : new Date(now.getTime() - 1440 * 60000)
const initialTo = props.modelValue?.to ? toWall(props.modelValue.to) : new Date(now.getTime())
const committed = reactive({ from: initialFrom, to: initialTo })

const popoverOpen = ref(false)
const draftStart = ref(new Date(committed.from))
const draftEnd = ref(new Date(committed.to))
const selectingEnd = ref(false)
const hoverDate = ref(null)
const viewYear = ref(committed.from.getFullYear())
const viewMonth = ref(committed.from.getMonth())

const rootEl = ref(null)

function pad(n) { return n < 10 ? '0' + n : '' + n }
function stripTime(d) { return new Date(d.getFullYear(), d.getMonth(), d.getDate()) }
function sameDay(a, b) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate() }
function formatFull(d) { return `${monthNames[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} \u00b7 ${pad(d.getHours())}:${pad(d.getMinutes())}` }
function formatDT(d) { return `${monthNames[d.getMonth()]} ${d.getDate()}, ${pad(d.getHours())}:${pad(d.getMinutes())}` }
function clampDate(d) {
  if (d.getTime() < floorDate.getTime()) return new Date(floorDate.getTime())
  if (d.getTime() > now.getTime()) return new Date(now.getTime())
  return d
}
function durationLabel(ms) {
  const mins = Math.round(ms / 60000)
  let d = Math.floor(mins / 1440)
  let rem = mins - d * 1440
  const h = Math.floor(rem / 60)
  const m = rem - h * 60
  const parts = []
  if (d) parts.push(`${d}d`)
  if (h) parts.push(`${h}h`)
  if (m || parts.length === 0) parts.push(`${m}m`)
  return parts.join(' ')
}

const monthLabel = computed(() => `${monthNames[viewMonth.value]} ${viewYear.value}`)

const previewEnd = computed(() => (selectingEnd.value && hoverDate.value) ? hoverDate.value : draftEnd.value)

const calendarCells = computed(() => {
  const y = viewYear.value
  const m = viewMonth.value
  const firstDow = new Date(y, m, 1).getDay()
  const daysInMonth = new Date(y, m + 1, 0).getDate()
  const prevDays = new Date(y, m, 0).getDate()
  const cells = []
  for (let i = 0; i < firstDow; i++) {
    const dnum = prevDays - firstDow + 1 + i
    cells.push({ day: dnum, inMonth: false, date: new Date(y, m - 1, dnum) })
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, inMonth: true, date: new Date(y, m, d) })
  }
  while (cells.length % 7 !== 0 || cells.length < 35) {
    const nd = cells.length - (firstDow + daysInMonth) + 1
    cells.push({ day: nd, inMonth: false, date: new Date(y, m + 1, nd) })
  }
  return cells.map((c, idx) => ({
    ...c,
    key: idx,
    disabled: c.date < minDay || c.date > maxDay
  }))
})

function dayClass(cell) {
  const s = stripTime(draftStart.value)
  const e = stripTime(previewEnd.value)
  const lo = s < e ? s : e
  const hi = s < e ? e : s
  const isEndpoint = sameDay(cell.date, lo) || sameDay(cell.date, hi)
  const inRange = cell.date > lo && cell.date < hi
  return {
    'is-out-of-month': !cell.inMonth,
    'is-disabled': cell.disabled,
    'is-endpoint': cell.inMonth && !cell.disabled && isEndpoint,
    'is-in-range': cell.inMonth && !cell.disabled && inRange
  }
}

const triggerLabel = computed(() => `${formatFull(committed.from)} \u2192 ${formatFull(committed.to)}`)
const summaryLabel = computed(() => activeIndex.value >= 0 ? presets.value[activeIndex.value].label : durationLabel(Math.max(committed.to - committed.from, 0)))
const summarySub = computed(() => `${formatDT(committed.from)} to ${formatDT(committed.to)}`)

const trackFillStyle = computed(() => {
  const trackStart = now.getTime() - maxMs
  const leftPct = Math.max(0, Math.min(100, (committed.from.getTime() - trackStart) / maxMs * 100))
  const rightPct = Math.max(0, Math.min(100, (committed.to.getTime() - trackStart) / maxMs * 100))
  return { left: leftPct + '%', width: Math.max(rightPct - leftPct, 0.8) + '%' }
})

function togglePopover() {
  if (popoverOpen.value) closePopover()
  else openPopover()
}
function openPopover() {
  draftStart.value = new Date(committed.from)
  draftEnd.value = new Date(committed.to)
  selectingEnd.value = false
  hoverDate.value = null
  viewYear.value = draftStart.value.getFullYear()
  viewMonth.value = draftStart.value.getMonth()
  popoverOpen.value = true
}
function closePopover() {
  popoverOpen.value = false
}

function navMonth(dir) {
  let m = viewMonth.value + dir
  let y = viewYear.value
  if (m < 0) { m = 11; y-- }
  if (m > 11) { m = 0; y++ }
  viewMonth.value = m
  viewYear.value = y
}

function pickDay(cell) {
  if (cell.disabled) return
  if (!selectingEnd.value) {
    const start = new Date(draftStart.value)
    start.setFullYear(cell.date.getFullYear(), cell.date.getMonth(), cell.date.getDate())
    const end = new Date(draftEnd.value)
    end.setFullYear(cell.date.getFullYear(), cell.date.getMonth(), cell.date.getDate())
    draftStart.value = start
    draftEnd.value = end
    selectingEnd.value = true
    hoverDate.value = null
  } else {
    const s = stripTime(draftStart.value)
    if (cell.date < s) {
      const newEnd = new Date(draftEnd.value)
      newEnd.setFullYear(draftStart.value.getFullYear(), draftStart.value.getMonth(), draftStart.value.getDate())
      const newStart = new Date(draftStart.value)
      newStart.setFullYear(cell.date.getFullYear(), cell.date.getMonth(), cell.date.getDate())
      draftEnd.value = newEnd
      draftStart.value = newStart
    } else {
      const end = new Date(draftEnd.value)
      end.setFullYear(cell.date.getFullYear(), cell.date.getMonth(), cell.date.getDate())
      draftEnd.value = end
    }
    selectingEnd.value = false
    hoverDate.value = null
  }
}

function hoverDay(cell) {
  if (!selectingEnd.value || cell.disabled) return
  if (!hoverDate.value || !sameDay(hoverDate.value, cell.date)) hoverDate.value = cell.date
}

function stepTime(target, unit, dir) {
  const src = target === 'start' ? draftStart : draftEnd
  const d = new Date(src.value)
  if (unit === 'h') d.setHours((d.getHours() + dir + 24) % 24)
  else d.setMinutes((d.getMinutes() + dir + 60) % 60)
  src.value = d
}

function emitChange(extra = {}) {
  const payload = { from: fromWall(committed.from), to: fromWall(committed.to), ...extra }
  emit('update:modelValue', payload)
  emit('apply', payload)
}

function applyRange() {
  let from = clampDate(draftStart.value)
  let to = clampDate(draftEnd.value)
  if (from.getTime() > to.getTime()) { [from, to] = [to, from] }
  committed.from = from
  committed.to = to
  activeIndex.value = -1
  closePopover()
  emitChange({ presetMins: null })
}

function selectPreset(i) {
  activeIndex.value = i
  const p = presets.value[i]
  if (!p.mins) {
    // Calendar preset: the parent owns the from/to math and feeds it back via
    // modelValue; keep the summary meaningful in the meantime.
    if (props.modelValue?.from) committed.from = toWall(props.modelValue.from)
    if (props.modelValue?.to) committed.to = toWall(props.modelValue.to)
    emitChange({ presetMins: null, presetKey: p.key || null, presetLabel: p.label })
    return
  }
  const to = new Date(now.getTime())
  const from = new Date(now.getTime() - p.mins * 60000)
  committed.from = from
  committed.to = to
  emitChange({ presetMins: p.mins, presetKey: p.key || null, presetLabel: p.label })
}

function handleOutsideClick(e) {
  if (popoverOpen.value && rootEl.value && !rootEl.value.contains(e.target)) {
    closePopover()
  }
}

onMounted(() => {
  document.addEventListener('click', handleOutsideClick)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', handleOutsideClick)
})
</script>

<style scoped>
.dtr {
  --dtr-bg: #10192e;
  --dtr-border: #232c46;
  --dtr-border-strong: #3a4568;
  --dtr-panel: #1a2340;
  --dtr-surface: #2d3755;
  --dtr-text: #e7eaf0;
  --dtr-text-secondary: #a9b0c2;
  --dtr-text-muted: #69708a;
  --dtr-text-dim: #3a4568;
  --dtr-accent: #0060f5;
  --dtr-accent-soft: #142a52;
  --dtr-accent-text: #5b9cff;
  --dtr-range-fill: #142a52;
  --dtr-range-text: #bfd4ff;

  background: var(--dtr-bg);
  border: 1px solid var(--dtr-border);
  border-radius: 12px;
  padding: 1.25rem;
  max-width: 640px;
  font-family: inherit;
  color: var(--dtr-text);
  box-sizing: border-box;
}
.dtr *, .dtr *::before, .dtr *::after { box-sizing: border-box; }

.dtr-icon { width: 18px; height: 18px; color: var(--dtr-text-muted); flex-shrink: 0; }
.dtr-icon-right { margin-left: auto; width: 14px; height: 14px; transition: transform 0.15s ease; }
.dtr-icon-right.is-flipped { transform: rotate(180deg); }

.dtr-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; gap: 12px; }
.dtr-header-left { display: flex; align-items: center; gap: 8px; min-width: 0; }
.dtr-summary-label { font-size: 15px; font-weight: 500; color: var(--dtr-text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dtr-summary-sub { font-size: 13px; color: var(--dtr-text-muted); white-space: nowrap; }

.dtr-track { height: 6px; background: var(--dtr-border); border-radius: 4px; position: relative; margin-bottom: 4px; overflow: hidden; }
.dtr-track-fill { position: absolute; top: 0; bottom: 0; background: var(--dtr-accent); border-radius: 4px; }
.dtr-track-labels { display: flex; justify-content: space-between; font-size: 11px; color: var(--dtr-text-muted); margin-bottom: 18px; }

.dtr-body { display: flex; gap: 20px; }
.dtr-col-label { font-size: 12px; color: var(--dtr-text-muted); margin-bottom: 8px; }

.dtr-presets { width: 172px; flex-shrink: 0; border-right: 1px solid var(--dtr-border); padding-right: 16px; display: flex; flex-direction: column; }
.dtr-preset-btn {
  text-align: left; width: 100%; font-size: 13px; padding: 6px 8px; margin-bottom: 2px;
  border: none; border-left: 2px solid transparent; border-radius: 0;
  background: transparent; color: var(--dtr-text-secondary); cursor: pointer;
}
.dtr-preset-btn:hover { background: rgba(255, 255, 255, 0.03); }
.dtr-preset-btn.is-active {
  border-left-color: var(--dtr-accent);
  background: var(--dtr-accent-soft);
  color: var(--dtr-accent-text);
  font-weight: 500;
}
.dtr-preset-btn:focus-visible { outline: 2px solid var(--dtr-accent); outline-offset: -2px; }

.dtr-custom { flex: 1; min-width: 0; }

.dtr-trigger {
  width: 100%; display: flex; align-items: center; gap: 6px;
  background: var(--dtr-surface); border: 1px solid var(--dtr-border-strong); color: var(--dtr-text);
  border-radius: 6px; padding: 8px 10px; font-size: 12px; cursor: pointer; text-align: left;
}
.dtr-trigger:focus-visible { outline: 2px solid var(--dtr-accent); outline-offset: 1px; }
.dtr-trigger-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.dtr-popover {
  margin-top: 8px; width: 100%;
  background: var(--dtr-panel); border: 1px solid var(--dtr-border-strong); border-radius: 10px; padding: 12px;
}

.dtr-cal-nav { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.dtr-nav-btn { background: none; border: none; color: var(--dtr-text-secondary); cursor: pointer; padding: 2px 6px; }
.dtr-nav-btn svg { width: 16px; height: 16px; }
.dtr-nav-btn:focus-visible { outline: 2px solid var(--dtr-accent); }
.dtr-month-label { font-size: 13px; color: var(--dtr-text); font-weight: 500; }
.dtr-cal-hint { font-size: 10px; color: var(--dtr-accent-text); margin-bottom: 8px; }

.dtr-cal-dow { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; margin-bottom: 2px; }
.dtr-cal-dow span { font-size: 10px; color: var(--dtr-text-muted); text-align: center; }

.dtr-cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; margin-bottom: 10px; }
.dtr-day-btn {
  font-size: 12px; text-align: center; padding: 5px 0; border: none; cursor: pointer;
  background: transparent; color: var(--dtr-text-secondary); border-radius: 6px;
}
.dtr-day-btn:hover:not(:disabled):not(.is-endpoint) { background: rgba(255, 255, 255, 0.04); }
.dtr-day-btn.is-out-of-month { color: var(--dtr-text-dim); }
.dtr-day-btn.is-disabled { color: var(--dtr-text-dim); cursor: not-allowed; }
.dtr-day-btn.is-endpoint { background: var(--dtr-accent); color: #ffffff; font-weight: 500; }
.dtr-day-btn.is-in-range { background: var(--dtr-range-fill); color: var(--dtr-range-text); border-radius: 0; }
.dtr-day-btn:focus-visible { outline: 2px solid var(--dtr-accent); outline-offset: 1px; }

.dtr-time-row { display: flex; align-items: flex-end; justify-content: space-between; border-top: 1px solid var(--dtr-border); padding-top: 10px; margin-bottom: 10px; }
.dtr-time-group { display: flex; flex-direction: column; }
.dtr-time-label { font-size: 10px; color: var(--dtr-text-muted); margin-bottom: 4px; }
.dtr-stepper { display: flex; align-items: center; gap: 4px; }
.dtr-stepper-unit { display: flex; flex-direction: column; align-items: center; }
.dtr-step-btn { background: none; border: none; color: var(--dtr-text-muted); cursor: pointer; padding: 0; }
.dtr-step-btn svg { width: 13px; height: 13px; }
.dtr-step-btn:focus-visible { outline: 2px solid var(--dtr-accent); }
.dtr-step-val { font-size: 14px; color: var(--dtr-text); width: 22px; text-align: center; font-variant-numeric: tabular-nums; }
.dtr-colon { color: var(--dtr-text-muted); font-size: 12px; }

.dtr-apply-btn {
  width: 100%; background: var(--dtr-accent); color: #ffffff; border: none; border-radius: 6px;
  padding: 8px 0; font-size: 12px; font-weight: 500; cursor: pointer;
}
.dtr-apply-btn:hover { background: #0052d6; }
.dtr-apply-btn:focus-visible { outline: 2px solid #ffffff; outline-offset: 2px; }

.dtr-note { font-size: 11px; color: var(--dtr-text-muted); margin-top: 8px; }

/* Stack presets above the custom range on narrow screens so nothing overflows. */
@media (max-width: 480px) {
  .dtr { padding: 1rem; }
  .dtr-header { flex-direction: column; align-items: flex-start; gap: 2px; }
  .dtr-body { flex-direction: column; gap: 14px; }
  .dtr-presets {
    width: 100%; border-right: none; padding-right: 0;
    border-bottom: 1px solid var(--dtr-border); padding-bottom: 12px;
  }
}
</style>
